import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { ChargeType } from "@/pages/settings/pages/products-v2/types";
import { formatCurrency } from "../consts";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";

interface PricingSummaryProps {
  total: number;
  quantity: number;
  // The unit price and period the line actually runs on, already resolved by
  // the form — an existing line's recorded terms, not the catalog's current
  // ones. null unitPrice means the line records no unit price, which collapses
  // the per-unit breakdown rather than printing a figure the line never had.
  unitPrice?: number | null;
  durationInMonths?: number;
  discount?: number;
  discountType?: "percentage" | "amount";
  discountAmount?: number;
  chargeType?: ChargeType;
  showOnlyTotal?: boolean;
  currency?: string | null;
}

export const PricingSummary: React.FC<PricingSummaryProps> = ({
  total,
  quantity,
  unitPrice,
  durationInMonths,
  discount = 0,
  discountType = "percentage",
  discountAmount = 0,
  chargeType,
  showOnlyTotal = false,
  currency: rawCurrency
}) => {
  const { t } = useTranslation();
  const isOneTimeCharge = chargeType === ChargeType.ONE_TIME;

  const price = unitPrice ?? 0;
  // A one-time charge covers a single period whatever the term says.
  const months = isOneTimeCharge ? 1 : (durationInMonths ?? 1);
  // Without a unit price there is no per-unit breakdown worth printing.
  const showBreakdown = !showOnlyTotal && unitPrice != null;

  const totalBeforeDiscount = price * quantity * months;
  // A downsell's discount carries the line's sign, so compare magnitude — a
  // negative discountAmount is still a discount worth showing.
  const hasDiscount = discount > 0 && discountAmount !== 0;

  // recordCurrencySignOverride is the contract's sign already resolved against the
  // account's, for the figures printed beside a ConvertedCurrency below.
  const {
    accountCurrencySymbol,
    recordCurrencySignOverride,
    showConversion,
    rate
  } =
    // The raw prop: defaulting before the comparison makes an unset contract
    // currency read as USD and offers a conversion against it.
    useCurrencyConversion(rawCurrency);
  const currency = rawCurrency ?? "USD";

  if (!showBreakdown) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex h-8 items-center justify-between px-1 py-0 w-full">
          <div className="flex gap-1 items-center px-0 py-2 w-25">
            <Text variant="bodySecondary">{t("contractTerms.total")}</Text>
          </div>
          <div className="flex gap-1 h-full items-center p-2">
            {/* Without a unit price there is no pre-discount figure to strike
                through — `price` would be 0, which reads as a real amount. */}
            {hasDiscount && unitPrice != null ? (
              <div className="flex items-center gap-1 line-through mr-2">
                <Text variant="bodySecondary">
                  {formatCurrency(price ?? 0, currency, undefined, recordCurrencySignOverride)}
                </Text>
                {showConversion && (price ?? 0) > 0 && (
                  <ConvertedCurrency
                    amount={price}
                    rate={rate}
                    accountCurrencySymbol={accountCurrencySymbol}
                  />
                )}
              </div>
            ) : null}
            <div className="flex items-center gap-1">
              <Text variant="body" className="font-semibold text-base">
                {formatCurrency(total ?? 0, currency, undefined, recordCurrencySignOverride)}
              </Text>
              {showConversion && (total ?? 0) > 0 && (
                <ConvertedCurrency
                  amount={total}
                  rate={rate}
                  accountCurrencySymbol={accountCurrencySymbol}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex h-8 items-center justify-between px-1 py-0 w-full">
        <div className="flex gap-1 items-center px-0 py-2 w-25">
          <Text variant="bodySecondary">{t("contractTerms.total")}</Text>
        </div>
        <div className="flex gap-1 h-full items-center p-2">
          {hasDiscount && (
            <div className="flex items-center gap-1 line-through mr-2">
              <Text variant="bodySecondary">
                {formatCurrency(
                  totalBeforeDiscount,
                  currency,
                  undefined,
                  recordCurrencySignOverride
                )}
              </Text>
              {showConversion && totalBeforeDiscount > 0 && (
                <ConvertedCurrency
                  amount={totalBeforeDiscount}
                  rate={rate}
                  accountCurrencySymbol={accountCurrencySymbol}
                />
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Text variant="body" className="font-semibold text-base">
              {formatCurrency(total, currency, undefined, recordCurrencySignOverride)}
            </Text>
            {showConversion && total > 0 && (
              <ConvertedCurrency
                amount={total}
                rate={rate}
                accountCurrencySymbol={accountCurrencySymbol}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex h-8 items-center justify-end px-1 py-0 w-full">
        <div className="flex gap-1 h-full items-center p-2">
          <div className="flex flex-col items-end justify-center">
            <div className="flex gap-1 items-center text-sm flex-wrap justify-end">
              <Text variant="body">{quantity}</Text>
              <Text variant="bodySecondary">({t("contractTerms.units")})</Text>
              <Text variant="body">x</Text>
              <Text variant="body">
                {formatCurrency(
                  price,
                  currency,
                  undefined,
                  recordCurrencySignOverride
                )}
              </Text>
              <Text variant="bodySecondary">
                {isOneTimeCharge
                  ? `(${t("contractTerms.perUnit")})`
                  : `(${t("contractTerms.perUnitMonth")})`}
              </Text>
              {!isOneTimeCharge && (
                <>
                  <Text variant="body">x</Text>
                  <Text variant="body">{months}</Text>
                  <Text variant="bodySecondary">
                    ({t("contractTerms.months")})
                  </Text>
                </>
              )}
              {hasDiscount && (
                <>
                  <Text variant="body">-</Text>
                  <Text variant="body">
                    {discountType === "amount"
                      ? formatCurrency(
                          discount,
                          currency,
                          undefined,
                          recordCurrencySignOverride
                        )
                      : `${discount}%`}
                  </Text>
                  <Text variant="bodySecondary">
                    ({t("contractTerms.discount")})
                  </Text>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
